import { useSyncExternalStore } from 'react';
import { mockRequirements } from '@/data/mock-requirements';
import type { Requirement } from '@/data/mock-requirements';

/**
 * Lightweight module-level store so the Intake list and the dedicated
 * `/intake/new` page share the same (mock, in-memory) requirements state.
 * A draft created on the new-request page therefore survives navigation
 * back to the list.
 */
interface IntakeState {
  requirements: Requirement[];
  selectedId: string | undefined;
}

let state: IntakeState = {
  requirements: mockRequirements,
  selectedId: mockRequirements[0]?.id,
};

const listeners = new Set<() => void>();

const emit = (): void => {
  for (const listener of listeners) listener();
};

const subscribe = (callback: () => void): (() => void) => {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
};

const getSnapshot = (): IntakeState => state;

export const setRequirements = (
  updater: Requirement[] | ((prev: Requirement[]) => Requirement[]),
): void => {
  const next =
    typeof updater === 'function' ? updater(state.requirements) : updater;
  state = { ...state, requirements: next };
  emit();
};

export const setSelectedId = (id: string | undefined): void => {
  if (state.selectedId === id) return;
  state = { ...state, selectedId: id };
  emit();
};

export const useIntakeStore = (): IntakeState =>
  useSyncExternalStore(subscribe, getSnapshot);
